import BodyType from 'owa-service/lib/contract/BodyType';
import convertInlineCssForHtml from './convertInlineCssForHtml';
import getDefaultComposeContentBlock from './getDefaultComposeContentBlock';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { ComposeType } from 'owa-editor-addin-plugin-types/lib/utils/AddinViewState';
import { ComposeViewState, ComposeOperation } from 'owa-mail-compose-store';
import { ContentPosition, InsertOption, PositionType } from 'roosterjs-editor-types';
import { format } from 'owa-localize';
import { getStore as getRoamingSignatureStore } from 'owa-mail-signature/lib/store/signatureStore';
import { isFeatureEnabled } from 'owa-feature-flags';
import { lazyOperateContent } from 'owa-editor';
import { Signature as RoamingSignature } from 'owa-mail-signature/lib/store/schema/SignatureStore';

// The capital 'S' is important to match jsMVVM
// TODO:45086 Move away from ID and use class to have properly formatted HTML.
const SIGNATURE_ID = 'Signature';
const SIGNATURE_SELECTOR = '#' + SIGNATURE_ID;

function insertSignature(viewState: ComposeViewState, isAutoSignature?: boolean);
function insertSignature(
    viewState: ComposeViewState,
    signature: RoamingSignature,
    replaceExisting?: boolean
);
function insertSignature(viewState: ComposeViewState, signature: string, replaceExisting?: boolean);

function insertSignature(
    viewState: ComposeViewState,
    param?: boolean | string | RoamingSignature,
    replaceExisting?: boolean
): Promise<void> {
    const {
        bodyType,
        operation,
        useSmartResponse,
        addin: { draftComposeType },
    } = viewState;
    const isAutoSignature = typeof param === 'boolean' ? param : false;
    let signature =
        typeof param === 'string'
            ? param
            : typeof param === 'object'
            ? getRoamingSignature(param, bodyType)
            : getDefaultSignature(
                  bodyType,
                  operation == ComposeOperation.Reply || operation == ComposeOperation.ReplyAll
              );

    return !replaceExisting && isSignatureEmpty(signature, bodyType)
        ? Promise.resolve()
        : lazyOperateContent.import().then(operateContent =>
              operateContent(
                  viewState,
                  (editor, range) => {
                      editor.focus();

                      const defaultContent = getDefaultComposeContentBlock();
                      const insertOption: InsertOption = {
                          position: ContentPosition.SelectionStart,
                          updateCursor: false,
                          replaceSelection: true,
                          insertOnNewLine: true,
                      };

                      signature = format("<div id='{0}'>{1}</div>", SIGNATURE_ID, signature);

                      editor.addUndoSnapshot(() => {
                          if (replaceExisting) {
                              // This will delete only the node with id "Sigature", it doesn't affest the quoted body
                              // beacuse for quoted body, server will change the element id from "Signature" to "x_Signature"
                              const nodes = editor.queryElements(SIGNATURE_SELECTOR);
                              if (nodes.length == 1) {
                                  editor.select(nodes[0], PositionType.Before);
                                  editor.deleteNode(nodes[0]);
                              } else {
                                  // We'd like to replace existing signature but no signature is found, so we can try to insert at end if possible
                                  // When smart response is used (quoted body is not in editor), or this is a new message (no quoted body)
                                  // we can insert at the end
                                  if (useSmartResponse || draftComposeType == ComposeType.New) {
                                      insertOption.position = ContentPosition.End;
                                  }

                                  signature = defaultContent + signature;
                              }
                          } else {
                              signature = defaultContent + signature;
                              if (isAutoSignature) {
                                  insertOption.position = ContentPosition.End;
                              }
                          }

                          signature = convertInlineCssForHtml(signature, 'HTML');
                          editor.insertContent(signature, insertOption);
                      }, 'Signature');
                      return range;
                  },
                  (content, selectionStart, selectionEnd) => ({
                      value:
                          content.substr(0, selectionEnd) +
                          '\n\n' +
                          signature +
                          content.substr(selectionEnd),
                      selectionStart,
                      selectionEnd,
                      focus: true,
                  })
              )
          );
}

export default insertSignature;

function getRoamingSignature(signature: RoamingSignature, bodyType: BodyType): string {
    const sigObj = bodyType == 'HTML' ? signature?.html : signature?.txt;
    return sigObj?.value || '';
}

function getDefaultSignature(bodyType: BodyType, isReply: boolean): string {
    if (isFeatureEnabled('cmp-roaming-signature')) {
        const store = getRoamingSignatureStore();

        let roamingSignatureName = isReply
            ? store.defaultReplySignatureName
            : store.defaultSignatureName;

        return getRoamingSignature(
            roamingSignatureName === ''
                ? store.legacySignature
                : store.roamingSignatureMap.get(roamingSignatureName),
            bodyType
        );
    } else {
        const userOptions = getUserConfiguration().UserOptions;
        return (bodyType == 'HTML' ? userOptions.SignatureHtml : userOptions.SignatureText) || '';
    }
}

function isSignatureEmpty(signature: string, bodyType: BodyType): boolean {
    if (bodyType == 'HTML') {
        // DOMParser returns a HTML document. Need to get the HTML document element;
        const checkEl = new DOMParser().parseFromString(signature, 'text/html').documentElement;
        const signatureImgElList = checkEl?.querySelectorAll('img[src]:not([src=""])') || [];
        const signatureInnerText = checkEl?.innerText.replace(/(\t|\n|\r|\v|\f)/g, '');

        return !signatureInnerText && signatureImgElList.length == 0;
    } else {
        return !signature;
    }
}
